import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Heading, Paragraph, CallToAction } from '@scentregroup/ui'
import ExternalLink from '../external-link'
import ResponsiveImage from '../responsive-image'
import { WhatsOnItem, WhatsOnEvent } from '../../../types/whats-on'
import Trackable from '../trackable'
import { trackingActions, trackingTypes } from '../../constants'
import styles from './whats-on-list-item.module.scss'

const isEvent = (item: WhatsOnItem): item is WhatsOnEvent =>
  Boolean((item as WhatsOnEvent).time)
const isBookable = (item: WhatsOnItem): item is WhatsOnEvent =>
  Boolean((item as WhatsOnEvent).bookingUrl)

export interface WhatsOnListItemProps {
  item: WhatsOnItem
}

const WhatsOnListItem = ({
  item,
}: WhatsOnListItemProps): React.ReactElement => (
  <Trackable
    {...item.analyticsMetadata}
    trackingAction={trackingActions.viewPromotion}
    type={trackingTypes.promotion}
  >
    <li className={styles.item}>
      <div className={styles.infoSection}>
        <Link to={item.slug} className={styles.itemAction}>
          <div className={styles.info}>
            <Heading type="extrasmall" className={styles.heading}>
              {item.name}
            </Heading>
            <Paragraph type="medium" className={styles.details}>
              {isEvent(item) ? item.time : item.dates}
            </Paragraph>
            <Paragraph type="medium" className={styles.details}>
              {isEvent(item) && item.price ? `${item.price} • ` : ''}
              {item.location}
            </Paragraph>
          </div>
        </Link>
        {isBookable(item) ? (
          <div className={styles.extraActions}>
            <Paragraph type="medium">
              <CallToAction
                className={styles.bookingButton}
                element={ExternalLink}
                url={item.bookingUrl}
              >
                Book now
              </CallToAction>
            </Paragraph>
          </div>
        ) : undefined}
      </div>
      <Link to={item.slug} className={styles.imageContainer}>
        <ResponsiveImage
          alt={item.image.description}
          desktopImageUrl={item.image.sourceDesktop}
          mobileImageUrl={item.image.sourceMobile}
          mobileRatio="square"
          desktopRatio="square"
          numberOfItems={3}
        />
        <div className={styles.type} data-test-id="whats-on-list-item-type">
          {isEvent(item) ? 'Event' : 'Offer'}
        </div>
      </Link>
    </li>
  </Trackable>
)

const ImagePropType = PropTypes.shape({
  sourceDesktop: PropTypes.string.isRequired,
  sourceMobile: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
})

export const EventPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  bookingUrl: PropTypes.string,
  image: ImagePropType.isRequired,
})

export const OfferPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  dates: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  image: ImagePropType.isRequired,
})

export const ItemPropType = PropTypes.oneOfType([EventPropType, OfferPropType])

WhatsOnListItem.propTypes = {
  item: ItemPropType.isRequired,
}

export default WhatsOnListItem
